import React from "react";
import Loader from "../Loader.tsx";

type PendingDocument = {
  id: number;
  documentName: string;
  source: string;
  requestType?: "upload" | "deletion";
  partnerRequestComment?: string | null;
  partner: {
    id: number;
    name: string;
    branch?: string | null;
    position?: string | null;
    curp?: string | null;
  };
  file: {
    id: string;
    extension?: string;
    name?: string;
  };
  fileUrl?: string;
  metadata?: Record<string, any>;
  queuedAt?: string;
  createdAt?: string;
};

interface DocumentsReviewTableProps {
  pendingDocs: PendingDocument[];
  loading: boolean;
  searchInput: string;
  setSearchInput: (value: string) => void;
  setSearch: (value: string) => void;
  searchInputRef:
    | React.RefObject<HTMLInputElement | null>
    | React.RefObject<HTMLInputElement>;
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: "ASC" | "DESC";
  setSortOrder: (value: "ASC" | "DESC") => void;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  total: number;
  queueType: "upload" | "deletion";
  authError?: string;
  onPreview: (doc: PendingDocument) => void;
  viewMode: "swipe" | "table";
}

const TABLE_LIMIT = 10;

const DocumentsReviewTable: React.FC<DocumentsReviewTableProps> = ({
  pendingDocs,
  loading,
  searchInput,
  setSearchInput,
  setSearch,
  searchInputRef,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  currentPage,
  setCurrentPage,
  total,
  queueType,
  authError,
  onPreview,
  viewMode,
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(field);
      setSortOrder("ASC");
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) {
      return (
        <i
          className="fa fa-sort text-muted"
          style={{ fontSize: "12px", marginLeft: "4px" }}
        />
      );
    }
    return sortOrder === "ASC" ? (
      <i
        className="fa fa-sort-up"
        style={{ fontSize: "12px", marginLeft: "4px", color: "#1a73e8" }}
      />
    ) : (
      <i
        className="fa fa-sort-down"
        style={{ fontSize: "12px", marginLeft: "4px", color: "#1a73e8" }}
      />
    );
  };

  const isDeletionQueue = queueType === "deletion";
  const totalPages = Math.ceil(total / TABLE_LIMIT);
  const hasSearch = searchInput.trim() !== "";

  if (loading) {
    return (
      <div
        className="bg-white rounded-4 p-5 text-center"
        style={{
          maxWidth: "520px",
          width: "100%",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Loader isLoading />
      </div>
    );
  }

  if (pendingDocs.length === 0) {
    return (
      <div
        className="bg-white rounded-4 overflow-hidden"
        style={{
          width: "100%",
          maxWidth: "1400px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <div className="p-4 border-bottom">
          <div className="d-flex align-items-center gap-3">
            <div className="flex-grow-1 position-relative">
              <i
                className="fa fa-search position-absolute"
                style={{
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#80868b",
                  fontSize: "14px",
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                className="form-control"
                placeholder="Buscar por nombre del colaborador..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                autoFocus={viewMode === "table"}
                style={{
                  paddingLeft: "36px",
                  fontSize: "14px",
                  border: "1px solid #dadce0",
                  borderRadius: "8px",
                }}
              />
            </div>
          </div>
        </div>
        <div className="p-5 text-center">
          <div
            className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "72px",
              height: "72px",
              background: "#f1f3f4",
            }}
          >
            <i
              className={hasSearch ? "fa fa-search" : "fa fa-check-circle"}
              style={{ fontSize: "32px", color: "#5f6368" }}
            />
          </div>
          <h4 className="fw-semibold mb-2" style={{ color: "#202124" }}>
            {authError
              ? "Sin autorización"
              : hasSearch
              ? "No se encontraron resultados"
              : queueType === "deletion"
              ? "Sin solicitudes"
              : "Todo listo"}
          </h4>
          <p className="mb-4" style={{ color: "#5f6368", fontSize: "14px" }}>
            {authError
              ? authError
              : hasSearch
              ? `No se encontraron documentos que coincidan con "${searchInput}"`
              : queueType === "deletion"
              ? "No hay solicitudes de rechazo pendientes"
              : "No hay documentos pendientes por revisar"}
          </p>
          {hasSearch && (
            <button
              className="btn px-4 py-2"
              style={{
                background: "#f1f3f4",
                color: "#1a73e8",
                fontWeight: 500,
                fontSize: "14px",
                border: "none",
                borderRadius: "8px",
              }}
              onClick={() => {
                setSearchInput("");
                setSearch("");
              }}
            >
              <i className="fa fa-times me-2" />
              Limpiar búsqueda
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-4 overflow-hidden"
      style={{
        width: "100%",
        maxWidth: "1400px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
      }}
    >
      <div className="p-4 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <div className="flex-grow-1 position-relative">
            <i
              className="fa fa-search position-absolute"
              style={{
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#80868b",
                fontSize: "14px",
              }}
            />
            <input
              ref={searchInputRef}
              type="text"
              className="form-control"
              placeholder="Buscar por nombre del colaborador..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoFocus={viewMode === "table"}
              style={{
                paddingLeft: "36px",
                fontSize: "14px",
                border: "1px solid #dadce0",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead style={{ background: "#f8f9fa" }}>
            <tr>
              <th
                style={{
                  padding: "12px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#5f6368",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
                onClick={() => handleSort("partner")}
              >
                Colaborador
                {getSortIcon("partner")}
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#5f6368",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
                onClick={() => handleSort("documentName")}
              >
                Documento
                {getSortIcon("documentName")}
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#5f6368",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
                onClick={() => handleSort("curp")}
              >
                CURP
                {getSortIcon("curp")}
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#5f6368",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
                onClick={() => handleSort("createdAt")}
              >
                Fecha
                {getSortIcon("createdAt")}
              </th>
              {isDeletionQueue && (
                <th
                  style={{
                    padding: "12px 16px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#5f6368",
                    textTransform: "uppercase",
                  }}
                >
                  Motivo
                </th>
              )}
              <th
                style={{
                  padding: "12px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#5f6368",
                  textTransform: "uppercase",
                  textAlign: "right",
                }}
              >
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {pendingDocs.map((doc) => (
              <tr key={doc.id} style={{ borderBottom: "1px solid #e8eaed" }}>
                <td style={{ padding: "16px" }}>
                  <div
                    className="fw-medium"
                    style={{ fontSize: "14px", color: "#202124" }}
                  >
                    {doc.partner?.name || "Sin nombre"}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#5f6368",
                      marginTop: "4px",
                    }}
                  >
                    {[doc.partner?.position, doc.partner?.branch]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </td>
                <td style={{ padding: "16px" }}>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="fw-medium"
                      style={{ fontSize: "14px", color: "#202124" }}
                    >
                      {doc.documentName}
                    </div>
                    {doc.source === "legacy" && (
                      <span
                        className="badge"
                        style={{
                          background: "#e8f0fe",
                          color: "#1967d2",
                          fontSize: "10px",
                          fontWeight: 500,
                          padding: "2px 8px",
                        }}
                      >
                        Legacy
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ padding: "16px" }}>
                  <div
                    className="fw-medium"
                    style={{
                      fontSize: "13px",
                      color: "#202124",
                      fontFamily: "monospace",
                    }}
                  >
                    {doc.partner?.curp || "—"}
                  </div>
                </td>
                <td style={{ padding: "16px" }}>
                  <div style={{ fontSize: "13px", color: "#202124" }}>
                    {formatDate(doc.createdAt || doc.queuedAt)}
                  </div>
                </td>
                {isDeletionQueue && (
                  <td style={{ padding: "16px" }}>
                    <div style={{ fontSize: "13px", color: "#78350f" }}>
                      {doc.partnerRequestComment?.trim()
                        ? doc.partnerRequestComment
                        : "El colaborador solicitó eliminar este documento para volver a subirlo."}
                    </div>
                  </td>
                )}
                <td style={{ padding: "16px", textAlign: "right" }}>
                  <button
                    className="btn btn-sm"
                    style={{
                      background: "#1a73e8",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      fontSize: "13px",
                      fontWeight: 500,
                    }}
                    onClick={() => onPreview(doc)}
                  >
                    <i className="fa fa-eye me-1" />
                    Ver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center p-3 border-top">
          <div style={{ fontSize: "14px", color: "#5f6368" }}>
            Mostrando {(currentPage - 1) * TABLE_LIMIT + 1} -{" "}
            {Math.min(currentPage * TABLE_LIMIT, total)} de {total} documentos
          </div>
          <div className="d-flex gap-2 align-items-center">
            <button
              className="btn btn-sm"
              style={{
                background: currentPage === 1 ? "#f1f3f4" : "#ffffff",
                color: currentPage === 1 ? "#80868b" : "#202124",
                border: "1px solid #dadce0",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "13px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <i className="fa fa-chevron-left me-1" />
              Anterior
            </button>
            <div className="d-flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className="btn btn-sm"
                    style={{
                      background:
                        currentPage === pageNum ? "#1a73e8" : "#ffffff",
                      color: currentPage === pageNum ? "white" : "#202124",
                      border: "1px solid #dadce0",
                      borderRadius: "6px",
                      minWidth: "32px",
                      padding: "6px 8px",
                      fontSize: "13px",
                    }}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="btn btn-sm"
              style={{
                background: currentPage === totalPages ? "#f1f3f4" : "#ffffff",
                color: currentPage === totalPages ? "#80868b" : "#202124",
                border: "1px solid #dadce0",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "13px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
            >
              Siguiente
              <i className="fa fa-chevron-right ms-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsReviewTable;
